import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { useIntl } from '../../../util/reactIntl';
import { manageDisableScrolling } from '../../../ducks/ui.duck';
import { hasReturningUser } from '../../../ducks/auth.duck';
import { profilePageParams } from '../../../util/userHelpers';

import { InlineTextButton, Modal, NamedLink } from '../../../components';

import IconHome from '../Topbar/icons/IconHome';
import IconSearch from '../Topbar/icons/IconSearch';
import IconMessages from '../Topbar/icons/IconMessages';
import IconUser from '../Topbar/icons/IconUser';
import IconPlus from '../Topbar/icons/IconPlus';

import TopbarSearchForm from '../Topbar/TopbarDesktop/TopbarSearchForm/TopbarSearchForm';

import { isMobileBottomNavHidden } from '../Topbar/navigationConfig';

import css from './MobileBottomNav.module.css';

// Slot config — order matches the Figma design (Home / Search / Sell /
// Messages / Profile). The Sell slot is rendered as a raised circular
// CTA; the rest are plain icon links. Favorites moved out of the bottom
// nav and live on the Topbar heart icon — messaging is more critical
// in the nav, matching second-hand fashion marketplace conventions
// (Vinted, Depop, eBay app).
const SLOTS = [
  {
    key: 'home',
    routeName: 'LandingPage',
    Icon: IconHome,
    labelId: 'MobileBottomNav.home',
  },
  {
    key: 'search',
    routeName: 'SearchPage',
    Icon: IconSearch,
    labelId: 'MobileBottomNav.search',
  },
  {
    key: 'sell',
    routeName: 'NewListingPage',
    Icon: IconPlus,
    labelId: 'MobileBottomNav.sell',
    variant: 'cta',
  },
  {
    key: 'messages',
    routeName: 'InboxPage',
    routeParams: { tab: 'sales' },
    Icon: IconMessages,
    labelId: 'MobileBottomNav.messages',
  },
  {
    key: 'profile',
    routeName: 'ProfileSettingsPage',
    Icon: IconUser,
    labelId: 'MobileBottomNav.profile',
  },
];

/**
 * Bottom-fixed mobile navigation bar.
 *
 * Layout: 5 slots (Home / Search / Sell / Favorites / Profile). The
 * "Sell" slot is a raised circular CTA in the marketplaceColor — its
 * container has the same width as the others so the row stays balanced,
 * but the circle is `position: absolute` so it floats above the bar.
 *
 * The component is mobile-only (display: none at viewportMedium+); the
 * pages embedding it should also hide the desktop footer at the same
 * breakpoint to avoid a double bottom.
 *
 * @param {Object} props
 * @param {Object} props.match – React Router match (active route detection)
 * @returns {JSX.Element}
 */
const MobileBottomNavComponent = props => {
  const { match, location } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(state => state.auth?.isAuthenticated);
  const currentUser = useSelector(state => state.user?.currentUser);
  // Read localStorage only after mount so SSR and the first client render
  // agree (no hydration mismatch on the profile link's href).
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const returningUser = mounted && hasReturningUser();
  // Same combined sale + order count the inbox red-dot reads. Used
  // to overlay a qualitative "you have something new" indicator on
  // the messages slot so users notice without having to open the tab.
  const messagesNotificationCount = useSelector(
    state =>
      (state.user?.currentUserSaleNotificationCount || 0) +
      (state.user?.currentUserOrderNotificationCount || 0)
  );
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  const activeRoute = match?.path;
  const onManageDisableScrolling = (id, disable) =>
    dispatch(manageDisableScrolling(id, disable));
  const closeSearchModal = () => setIsSearchModalOpen(false);

  // Suppress the bottom nav on the account/side-nav pages and on the
  // listing family (listing detail, checkout, make-offer, edit-listing
  // wizard) — focused task flows where the global bottom nav is just
  // chrome in the way.
  if (isMobileBottomNavHidden(location?.pathname)) {
    return null;
  }

  const renderNavSlots = () =>
    SLOTS.map(({ key, routeName: defaultRouteName, routeParams, Icon, labelId, variant }) => {
          const isCta = variant === 'cta';
          const isSearch = key === 'search';
          const label = intl.formatMessage({ id: labelId });

          // Profile slot routing:
          // - Signed in  → the user's public profile (listings + reviews),
          //   not the edit-profile page.
          // - Signed out → Logg inn for returning users (this device has
          //   authenticated before), Opprett konto for first-timers.
          let routeName = defaultRouteName;
          let slotParams = routeParams;
          if (key === 'profile') {
            if (isAuthenticated && currentUser?.id) {
              routeName = 'ProfilePage';
              slotParams = profilePageParams(currentUser);
            } else if (isAuthenticated) {
              // Authenticated but currentUser not loaded yet — keep a safe
              // destination until we can resolve the public profile params.
              routeName = 'ProfileSettingsPage';
            } else {
              routeName = returningUser ? 'LoginPage' : 'SignupPage';
              slotParams = undefined;
            }
          }

          if (isCta) {
            return (
              <NamedLink
                key={key}
                className={css.ctaSlot}
                name={routeName}
                params={slotParams}
                ariaLabel={label}
                onClick={closeSearchModal}
              >
                <span className={css.ctaCircle}>
                  <Icon className={css.ctaIcon} />
                </span>
              </NamedLink>
            );
          }

          // The Search slot opens a centred modal with the same keyword
          // form rather than navigating straight to /s — mirrors the
          // standard Sharetribe Web Template mobile search pattern and
          // matches what we do on desktop (inline form), just adapted
          // for the smaller viewport where a fullscreen modal reads as
          // the natural search surface.
          if (isSearch) {
            return (
              <InlineTextButton
                key={key}
                className={css.slot}
                aria-label={label}
                aria-expanded={isSearchModalOpen}
                onClick={() => setIsSearchModalOpen(true)}
              >
                <Icon className={css.icon} />
              </InlineTextButton>
            );
          }

          const isActive = activeRoute && activeRoute.includes(routeName);
          const showMessagesBadge = key === 'messages' && messagesNotificationCount > 0;
          return (
            <NamedLink
              key={key}
              className={classNames(css.slot, { [css.slotActive]: isActive })}
              name={routeName}
              params={slotParams}
              ariaLabel={label}
              onClick={closeSearchModal}
            >
              <Icon className={css.icon} />
              {showMessagesBadge ? (
                <span className={css.messagesBadge} aria-hidden="true">
                  {messagesNotificationCount > 99
                    ? intl.formatMessage({ id: 'FavoritesBadge.overflow' })
                    : intl.formatNumber(messagesNotificationCount)}
                </span>
              ) : null}
            </NamedLink>
          );
    });

  return (
    <>
      <nav
        className={css.root}
        aria-label={intl.formatMessage({ id: 'MobileBottomNav.ariaLabel' })}
      >
        {renderNavSlots()}
      </nav>

      <Modal
        id="MobileBottomNav.search"
        isOpen={isSearchModalOpen}
        onClose={closeSearchModal}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
      >
        <div className={css.searchModalContent}>
          <h2 className={css.searchModalHeading}>
            {intl.formatMessage({ id: 'MobileBottomNav.searchHeading' })}
          </h2>
          <TopbarSearchForm
            onClose={closeSearchModal}
            className={css.searchModalForm}
            placeholder={intl.formatMessage({ id: 'MobileBottomNav.searchPlaceholder' })}
          />
        </div>
        {/* Render the nav again inside the modal (in the portal) so it
            reliably sits above it — a fixed nav outside the portal can't
            out-stack a portalled modal. Tapping a slot closes the modal. */}
        <nav
          className={css.modalNav}
          aria-label={intl.formatMessage({ id: 'MobileBottomNav.ariaLabel' })}
        >
          {renderNavSlots()}
        </nav>
      </Modal>
    </>
  );
};

const MobileBottomNav = withRouter(MobileBottomNavComponent);

export default MobileBottomNav;
